import {Button, Dialog, DialogBody, DialogFooter, DialogHeader, Typography} from "@material-tailwind/react";
import Dict from "../../helper/dict";
import React, {useContext, useEffect, useState} from "react";
import "../../style/datePicker.css"
import {ServiceContext} from "../../context/service";
import {PersianNumberInput} from "../system/persianNumberInput";
import persianNumberFormat from "../../helper/PersianNumberFormat";
import {updateServiceStep, updateServiceStepDone} from "../../services/service/service_step";
import {Toaster} from "../../helper/Toaster";
import FileInput from "../system/fileInput";

export function AgentServiceStepForm() {

  const {
    serviceStep,
    setServiceStep,
    serviceSteps,
    service,
    document,
    modalOpen,
    setModalOpen,
    setLastDataRefresh,
    setStep,
    setServiceStepsUpdated,
    setShow,
  } = useContext(ServiceContext);

  const handleOpen = () => setModalOpen(!modalOpen);


  const [sideCost, setSideCost] = useState(serviceStep.side_cost);
  const [days, setDays] = useState(serviceStep.days);
  const [file, setFile] = useState(serviceStep.file ?? "");
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);

  // The "done" action is only offered for in-progress services on a step that isn't done yet.
  const canMarkDone = service.status !== "new" && service.status !== "finish" && !serviceStep.is_done;

  // Proof-of-work upload is only required on the LAST step of the service (highest step number).
  const isLastStep = serviceSteps && serviceSteps.length > 0 &&
    Math.max(...serviceSteps.map(s => s.step)) === serviceStep.step;
  const requiresFile = canMarkDone && isLastStep;

  useEffect(() => {
    setSideCost(serviceStep.side_cost);
    setDays(serviceStep.days);
    setFile(serviceStep.file ?? "");

    if (serviceStep.id) {
      setLoaded(true);
    } else {
      setLoaded(false);
    }
  }, [serviceStep]);

  useEffect(() => {
    setServiceStep(serviceStep => ({
      ...serviceStep,
      days: days,
    }))
  }, [days]);

  useEffect(() => {
    setServiceStep(serviceStep => ({
      ...serviceStep,
      side_cost: sideCost,
    }))
  }, [sideCost]);

  const save = async () => {
    if (loading) return; // Prevent multiple simultaneous requests
    
    const data = serviceStep;
    
    try {
      setLoading(true);
      let result = await updateServiceStep(data);

      if (result.status === 201 || result.status === 202) {
        setServiceStepsUpdated(Date.now())
        setLastDataRefresh(Date.now())
        setStep("check")
      } else {
        Toaster.failure(null, <Typography variant="small">
          {result.data?.message || 'خطا در ذخیره مرحله'}
        </Typography>);
      }
    } catch (err) {
      Toaster.failure(null, <Typography variant="small">
        {err.message || 'خطا در ذخیره مرحله'}
      </Typography>);
      console.error('Service step save error:', err);
    } finally {
      setLoading(false);
    }
  };

  const save_done = async () => {
    if (loading) return; // Prevent multiple simultaneous requests

    if (requiresFile && !file) {
      Toaster.failure(null, <Typography variant="small">
        برای ثبت انجام مرحله آخر، بارگذاری مدرک الزامی است
      </Typography>);
      return;
    }

    const data = {...serviceStep, file};

    try {
      setLoading(true);
      let result = await updateServiceStepDone(data);

      if (result.status === 201 || result.status === 202) {
        setServiceStepsUpdated(Date.now())
        setLastDataRefresh(Date.now())
        setStep("check")
        setShow(2);
      } else {
        Toaster.failure(null, <Typography variant="small">
          {result.data?.message || 'خطا در ثبت مرحله'}
        </Typography>);
      }
    } catch (err) {
      Toaster.failure(null, <Typography variant="small">
        {err.message || 'خطا در ثبت مرحله'}
      </Typography>);
      console.error('Service step done error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={modalOpen} handler={handleOpen}
              animate={{mount: {scale: 1, y: 0}, unmount: {scale: 0.9, y: -100}}}>
        <DialogHeader className="border-b">
          {serviceStep &&
            <Typography>
              {service.person_name + " " + service.person_family + " - " + service.field_title + " " + service.branch_title + " ( " + serviceStep.title + " )"}
            </Typography>
          }
          {!serviceStep &&
            <Typography>ثبت مدرک جدید</Typography>
          }
        </DialogHeader>
        {loaded &&
          <DialogBody className="flex flex-col gap-4 overflow-y-scroll">

            <div className="flex flex-row mb-2 pb-2 border-b-2">
              <Typography color="blue-gray">
                {`${Dict("service_step")}: ${persianNumberFormat(serviceStep.step)}`}
              </Typography>
            </div>

            <div className="flex flex-row mb-2 pb-2 border-b-2">
              <Typography color="blue-gray">
                {`${Dict("title")}: ${serviceStep.title}`}
              </Typography>
            </div>

            <div className="flex flex-row mb-2 pb-2 border-b-2">
              <Typography color="blue-gray">
                {`${Dict("description")}: ${serviceStep.description}`}
              </Typography>
            </div>

            <div className="flex flex-row mb-4">
              <PersianNumberInput value={days} label={Dict("days")} setValue={setDays} extension="روز"/>
            </div>

            {service.status === "new" && service.status === "finish" && !serviceStep.is_done &&
              <div className="flex flex-row mb-4">
                <Typography color="blue-gray">
                  {`${Dict("is_done")}: ${serviceStep.is_done ? "بله" : "خیر"}`}
                </Typography>
              </div>
            }

            {requiresFile &&
              <div className="flex flex-col w-full mb-4 pt-2 border-t-2">
                <Typography color="blue-gray" className="font-medium mb-2">
                  این مرحله آخر است؛ برای ثبت انجام آن، مدرک مربوطه را بارگذاری کنید
                </Typography>
                <FileInput
                  label={Dict('upload_file')}
                  fileCode={file}
                  onChange={(e) => {
                    setFile(e.target.files[0]);
                  }}/>
              </div>
            }
          </DialogBody>
        }

        <DialogFooter className="space-x-2">
          <Button variant="text" color="gray" onClick={() => {
            // handleOpen();
            setStep("check")
          }}>
            <span>انصراف</span>
          </Button>


          {service.status === "new" && !serviceStep.is_done &&
            <Button variant="gradient" color="gray" className="ml-1" onClick={save} disabled={loading}>
              {loading ? 'در حال ذخیره...' : 'ذخیره'}
            </Button>
          }
          {canMarkDone &&
            <Button variant="gradient" color="gray" className="ml-1" onClick={save_done} disabled={loading || (requiresFile && !file)}>
              {loading ? 'در حال ثبت...' : 'انجام شده'}
            </Button>
          }
        </DialogFooter>
      </Dialog>

    </>
  );
}